import { TOAST_UPDATE_PERSONAL_INFO } from '@/constants/toast';
import { useMe } from '@/context/me/me.context';
import useAnalyticsCalls from '@/hooks/use-analytics';
import {
  useCancelSubscriptionMutation,
  useGetMembershipStatus,
  useRenewSubscriptionMutation,
} from '@/queries/membership.queries';
import { useDisclosure, useToast } from '@cmpsr/components';

export default function useEditMembershipController() {
  const toast = useToast();
  const { data: userData } = useMe();
  const membershipId = userData?.membership.id as string;

  const { data: membershipData } = useGetMembershipStatus(membershipId);
  const type = membershipData?.type as string;
  const cancelAtPeriodEnd = membershipData?.cancelAtPeriodEnd as boolean;
  const price = (membershipData?.price?.amount as number) ?? 0;
  const usdPrice = price / 100;
  const date = new Date(membershipData?.renewalDate as string);
  const analytics = useAnalyticsCalls();

  let renewalDate = '';

  if (membershipData?.renewalDate) {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    renewalDate = new Intl.DateTimeFormat('en-US', options).format(date);
  }

  const cancelSubscriptionMutation = useCancelSubscriptionMutation();
  const renewSubscriptionMutation = useRenewSubscriptionMutation();

  const {
    isOpen: showCancelMembershipModal,
    onOpen: onOpenShowCancelMembershipModal,
    onClose: onHideCancelMembershipModal,
  } = useDisclosure();

  const {
    isOpen: showRenewMembershipModal,
    onOpen: onOpenShowRenewMembershipModal,
    onClose: onHideRenewMembershipModal,
  } = useDisclosure();

  const onShowCancelMembershipModal = () => {
    onOpenShowCancelMembershipModal();
    analytics.callMembershipCancel();
  };

  const onShowRenewMembershipModal = () => {
    onOpenShowRenewMembershipModal();
    analytics.callMembershipRenew();
  };

  function handleCancelMembership() {
    return cancelSubscriptionMutation
      .mutateAsync(membershipId)
      .then(() => {
        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          description: 'Your membership was successfully cancelled',
          status: 'success',
        });
        onHideCancelMembershipModal();
      })
      .catch((error) => {
        console.error(error);
        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          title: `Error ${error.response?.data?.statusCode ?? ''}`,
          description: error.response?.data?.message ?? error.message,
          status: 'error',
        });
      });
  }

  function handleRenewMembership() {
    return renewSubscriptionMutation
      .mutateAsync(membershipId)
      .then(() => {
        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          description: 'Your membership was successfully renewed',
          status: 'success',
        });
        onHideRenewMembershipModal();
      })
      .catch((error) => {
        console.error(error);
        toast({
          ...TOAST_UPDATE_PERSONAL_INFO,
          title: `Error ${error.response?.data?.statusCode ?? ''}`,
          description: error.response?.data?.message ?? error.message,
          status: 'error',
        });
      });
  }

  const isTrialUser = userData?.membership?.status === 'trialing';

  return {
    type,
    cancelAtPeriodEnd,
    usdPrice,
    renewalDate,
    showCancelMembershipModal,
    onShowCancelMembershipModal,
    onHideCancelMembershipModal,
    handleCancelMembership,
    handleRenewMembership,
    showRenewMembershipModal,
    onShowRenewMembershipModal,
    onHideRenewMembershipModal,
    isTrialUser,
  };
}
